import { useState, useMemo } from "react";
import {
  DocumentIcon,
  PencilIcon,
  TrashIcon,
  CameraIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { SettingsHeader } from "../index";
import { Button, Modal, SettingsPanel, Spinner } from "../../../shared";
import {
  useDeleteTaskTemplateMutation,
  useGetTaskTemplatesQuery,
} from "../../../redux/services/taskTemplates/api";
import TaskTemplateModal from "./TaskTemplateModal";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks/useConfig";
import GroupsTab from "./GroupsTab";

// Category colors
const categoryColors = {
  pickup: "bg-blue-100 text-blue-800",
  delivery: "bg-green-100 text-green-800",
  documentation: "bg-indigo-100 text-indigo-800",
  inspection: "bg-amber-100 text-amber-800",
  safety: "bg-red-100 text-red-800",
  customer: "bg-purple-100 text-purple-800",
  general: "bg-gray-100 text-gray-800",
  arrival: "bg-teal-100 text-teal-800",
  diagnosis: "bg-yellow-100 text-yellow-800",
  work: "bg-orange-100 text-orange-800",
  completion: "bg-emerald-100 text-emerald-800",
  pre_trip: "bg-sky-100 text-sky-800",
  post_trip: "bg-slate-100 text-slate-800",
};

const TaskTemplates = ({ embedded = false }) => {
  const [activeTab, setActiveTab] = useState("templates");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [template, setTemplate] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const { appType } = useConfig();
  const { data: templates, isLoading, isError } = useGetTaskTemplatesQuery({});
  const [deleteTemplate] = useDeleteTaskTemplateMutation();

  // Map appType to industry for filtering
  const industryMap = {
    trucking: "trucking",
    serviceRepair: "service_repair",
  };
  const currentIndustry = industryMap[appType] || null;

  const handleEditTemplate = (template) => {
    setTemplate(template);
    setShowTemplateModal(true);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTemplate(templateToDelete._id).unwrap();
      toast.success("Template deleted successfully");
      setShowConfirmModal(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleCleanUp = () => {
    setShowTemplateModal(false);
    setTemplate(null);
  };

  // Filter templates to only those relevant to current appType
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    if (!currentIndustry) return templates; // Show all if no appType set
    return templates.filter(
      (t) =>
        t.industry === currentIndustry ||
        t.industry === "general" ||
        !t.industry
    );
  }, [templates, currentIndustry]);

  // Sort filtered templates by category then order
  const sortedTemplates = useMemo(() => {
    return [...filteredTemplates].sort((a, b) => {
      // First sort by category
      const categoryOrder = [
        "pre_trip",
        "pickup",
        "arrival",
        "diagnosis",
        "work",
        "delivery",
        "completion",
        "post_trip",
        "inspection",
        "safety",
        "documentation",
        "customer",
        "general",
      ];
      const aIdx = categoryOrder.indexOf(a.category) ?? 999;
      const bIdx = categoryOrder.indexOf(b.category) ?? 999;
      if (aIdx !== bIdx) return aIdx - bIdx;
      // Then by order within category
      return (a.order || 0) - (b.order || 0);
    });
  }, [filteredTemplates]);

  // Render template item
  const renderTemplateItem = (template) => ({
    title: (
      <div className="flex items-center gap-2">
        <span className="font-medium">{template.title || template.name}</span>
        {template.scope === "system" ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
            System
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
            Custom
          </span>
        )}
      </div>
    ),
    subtitle: (
      <div className="space-y-1">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              categoryColors[template.category] || categoryColors.general
            }`}
          >
            {template.category}
          </span>
          <span>
            Applies to:{" "}
            {template.appliesTo === "both"
              ? "Jobs & Legs"
              : template.appliesTo}
          </span>
        </div>
        {template.description && (
          <p className="text-sm text-gray-500">{template.description}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {template.required && (
            <span className="flex items-center gap-1">
              <CheckCircleIcon className="w-3 h-3" /> Required
            </span>
          )}
          {template.requiresPhoto && (
            <span className="flex items-center gap-1">
              <CameraIcon className="w-3 h-3" /> Photo
            </span>
          )}
          {template.requiresSignature && (
            <span className="flex items-center gap-1">
              <PencilSquareIcon className="w-3 h-3" /> Signature
            </span>
          )}
          {template.requiresNote && (
            <span className="flex items-center gap-1">
              <DocumentTextIcon className="w-3 h-3" /> Note
            </span>
          )}
          {template.autoApply && (
            <span className="text-green-600">Auto-apply</span>
          )}
        </div>
      </div>
    ),
    content: (
      <div className="flex items-center gap-2">
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => handleEditTemplate(template)}
          title="Edit Template"
        >
          <PencilIcon className="w-4 h-4 text-gray-500" />
        </button>
        {template.scope !== "system" && (
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={() => handleDeleteClick(template)}
            title="Delete Template"
          >
            <TrashIcon className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>
    ),
  });

  const getTemplateDisplayData = () => {
    if (sortedTemplates.length === 0) {
      return [
        {
          content: (
            <div className="my-10 text-center">
              <DocumentIcon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
              <h3 className="mt-2 font-semibold text-sm">No Templates</h3>
              <p className="text-sm text-gray-500">
                Get started by creating your first task template
              </p>
            </div>
          ),
        },
      ];
    }

    return sortedTemplates.map(renderTemplateItem);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
        <span className="ml-2">Loading templates...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading templates</p>
        <p className="text-sm text-gray-500 mt-1">
          Please check your connection and try again
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {!embedded && <SettingsHeader title="Task Templates" />}

        {/* Tabs: Templates | Groups */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              activeTab === "templates"
                ? "bg-primary-600 text-white"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab("groups")}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              activeTab === "groups"
                ? "bg-primary-600 text-white"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Groups
          </button>
        </div>
      </div>

      {activeTab === "groups" ? (
        <GroupsTab />
      ) : (
        <>
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center">
                  <DocumentIcon className="w-8 h-8 text-blue-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Total Templates
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {filteredTemplates.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-8 h-8 text-green-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      System Templates
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {
                        filteredTemplates.filter((t) => t.scope === "system")
                          .length
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center">
                  <PencilSquareIcon className="w-8 h-8 text-purple-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Custom Templates
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {
                        filteredTemplates.filter((t) => t.scope === "company")
                          .length
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center">
                  <CameraIcon className="w-8 h-8 text-orange-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Require Photo
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {filteredTemplates.filter((t) => t.requiresPhoto).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Templates List */}
            <SettingsPanel
              title="Task Templates"
              data={getTemplateDisplayData()}
              actions={
                <Button
                  size="xs"
                  onClick={() => {
                    setTemplate({ industry: currentIndustry || "general" });
                    setShowTemplateModal(true);
                  }}
                >
                  Add Template
                </Button>
              }
            />
          </div>

          {/* Template Modal */}
          <TaskTemplateModal
            editMode={!!template?._id}
            template={template}
            visible={showTemplateModal}
            setVisible={setShowTemplateModal}
            onClose={handleCleanUp}
            onSubmit={handleCleanUp}
          />

          {/* Delete Confirmation Modal */}
          <Modal
            title="Delete Template"
            visible={showConfirmModal}
            setVisible={setShowConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            confirm={handleDeleteConfirm}
            submitText="Delete"
          >
            <p>
              Are you sure you want to delete "
              <strong>
                {templateToDelete?.title || templateToDelete?.name}
              </strong>
              "?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This action cannot be undone.
            </p>
          </Modal>
        </>
      )}
    </>
  );
};

export default TaskTemplates;
